import { mockService } from './mockService';
import { csrfService } from './csrfService';

interface ApiService {
  // Generic HTTP methods
  get(endpoint: string, options?: { params?: Record<string, any> }): Promise<any>;
  post(endpoint: string, data?: any): Promise<any>;
  put(endpoint: string, data?: any): Promise<any>;
  delete(endpoint: string): Promise<any>;
  
  getFileContent(s3Uri: string): Promise<string>;
  getPractices(): Promise<any>;
  getWorkflows(): Promise<any>;
  getPracticeDetails(practiceId?: string): Promise<any>;
  getWorkflowsForPractice(practiceId: string): Promise<any>;
  uploadFiles(files: File[], practice: string, number: string): Promise<any>;
  uploadFileToPresignedUrl(presignedUrl: string, file: File): Promise<void>;
  executeWorkflow(params: any): Promise<any>;
  getExecutionStatus(executionArn: string): Promise<any>;
  getExecutionResults(executionArn: string): Promise<any>;
  getSignedDownloadUrl(s3Uri: string, filename?: string): Promise<string>;
  downloadFileBlob?(s3Uri: string): Promise<Blob>;
  getPodcastAudioUrl?(s3Key: string): Promise<{ presigned_url: string; expires_in: number }>;
  getSignedViewUrl?(s3Uri: string): Promise<{ signedUrl: string }>;
  submitUserInput(executionArn: string, inputData: Record<string, any>): Promise<any>;
  getAllJobs(): Promise<any>;
  pauseJob(jobId: string): Promise<any>;
  resumeJob(jobId: string): Promise<any>;
  cancelJob(jobId: string): Promise<any>;
  renameJob(jobId: string, newName: string): Promise<any>;
  deleteJob(jobId: string): Promise<any>;
  getRecipeRequirements(practice: string, workflow: string): Promise<any>;
  mapSupportedFormatsToMimeTypes?(formats: string[] | null): string[] | null;
  // AI Chat methods
  getChatConversations?(): Promise<any>;
  createChatConversation?(title: string): Promise<any>;
  getChatConversation?(conversationId: string): Promise<any>;
  deleteChatConversation?(conversationId: string): Promise<any>;
  getChatMessages?(conversationId: string): Promise<any>;
  sendChatMessage?(conversationId: string, content: string, model?: string, attachments?: any[], agentConfig?: any): Promise<any>;
  searchChatConversations?(query: string): Promise<any>;
  compressChatHistory?(conversationId: string): Promise<any>;
  // Document analysis
  analyzeDocument(fileName: string, s3Uri: string): Promise<any>;
  analyzeDocuments(files: { fileName: string; s3Uri: string }[]): Promise<any>;
  saveJsonToS3(jsonData: any, jobName: string, options?: { practice?: string; wrapInResponses?: boolean }): Promise<any>;
  saveFileContent(s3Uri: string, content: string): Promise<{ success: boolean; s3Uri: string }>;
  // Form schemas
  getSidebarForms?(): Promise<any>;
  getFormSubmissions?(params?: { schemaId?: string; limit?: number; offset?: number }): Promise<any>;
  // AI Workflow Builder
  createAiBuilderSession?(practice: string, workflow: string, title?: string): Promise<any>;
  listAiBuilderSessions?(): Promise<any>;
  getAiBuilderSession?(sessionId: string): Promise<any>;
  deleteAiBuilderSession?(sessionId: string): Promise<any>;
  getAiBuilderVersionFiles?(sessionId: string, versionNumber: number): Promise<any>;
  sendAiBuilderMessage?(sessionId: string, content: string, onChunk: (data: any) => void): Promise<void>;
  saveAiBuilderManualEdit?(sessionId: string, files: Record<string, any>): Promise<any>;
  revertAiBuilderVersion?(sessionId: string, versionId: number): Promise<any>;
  deployAiBuilderSession?(sessionId: string): Promise<any>;
}

// Check if we should use mock data
const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';


// Helper to safely join base URL and endpoint path
function joinUrl(base: string, path: string) {
  if (!base) return path;
  if (!path) return base;
  
  // Remove trailing slashes from base
  const cleanBase = base.replace(/\/+$/, '');
  // Ensure path starts with exactly one slash
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${cleanBase}${cleanPath}`;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3002').replace(/\/+$/, '');

// Token validation utility
const isTokenExpired = (token: string): boolean => {
  try {
    if (!token || !token.includes('.')) {
      return true;
    }
    
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Add 1 minute buffer for token refresh
    const bufferTime = 60; // 1 minute
    
    return payload.exp < (currentTime + bufferTime);
  } catch {
    return true;
  }
};

const extractErrorMessage = (raw: string): string => {
  if (!raw) {
    return '';
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      return parsed;
    }
    if (parsed?.message) {
      return parsed.message;
    }
    if (parsed?.error) {
      return parsed.error;
    }
    return JSON.stringify(parsed);
  } catch {
    return raw;
  }
};

// Enhanced fetch function with error handling
const enhancedFetch = async (url: string, options: RequestInit = {}) => {
  try {
    const response = await fetch(url, options);
    
    // Handle authentication errors immediately
    if (response.status === 401) {
      console.warn('Authentication failed, clearing session');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userData');

      // Redirect to login - this will trigger auth provider to handle it
      window.location.href = '/';
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      const errorText = extractErrorMessage(await response.clone().text());
      const error: any = new Error(errorText || 'Access denied');
      error.status = 403;
      throw error;
    }
    
    if (!response.ok) {
      const errorText = extractErrorMessage(await response.clone().text());
      const message = errorText ? `HTTP ${response.status}: ${errorText}` : `HTTP ${response.status}`;
      const error: any = new Error(message);
      error.status = response.status;
      throw error;
    }
    
    return response;
  } catch (error) {
    // Re-throw authentication errors
    if (error instanceof Error && error.message.includes('Authentication required')) {
      throw error;
    }
    
    // Handle network errors
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('Network error - please check your connection');
    }
    
    throw error;
  }
};

// Get auth headers for API requests
// Note: For Azure AD users, 'accessToken' contains the session token (UUID)
// For Cognito users, 'accessToken' contains the Cognito access token (JWT)
const getAuthHeaders = async (includeCSRF = false) => {
  const token = localStorage.getItem('accessToken');
  
  // Validate token before using it (skip validation for session tokens/UUIDs)
  if (token && token.includes('.') && isTokenExpired(token)) {
    console.warn('Token expired during API call preparation');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userData');
    
    // Redirect to login
    window.location.href = '/';
    throw new Error('Authentication required');
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
  
  if (includeCSRF) {
    try {
      const csrfToken = await csrfService.getToken();
      headers['X-CSRF-Token'] = csrfToken;
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
    }
  }
  
  return headers;
};

// Real API service implementation
const realApiService = {
  // Generic HTTP methods
  async get(endpoint: string, options?: { params?: Record<string, any> }) {
    // Append query parameters if provided
    let urlWithParams = endpoint;
    if (options?.params) {
      const queryString = new URLSearchParams(
        Object.entries(options.params)
          .filter(([_, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)])
      ).toString();
      if (queryString) {
        urlWithParams += (endpoint.includes('?') ? '&' : '?') + queryString;
      }
    }
    const fullUrl = joinUrl(API_BASE_URL, urlWithParams);
    console.log('[apiService.get] Request:', { endpoint, params: options?.params, fullUrl });
    
    try {
      const headers = await getAuthHeaders();
      console.log('[apiService.get] Headers:', { hasAuth: !!headers['Authorization'] });
      
      const response = await enhancedFetch(fullUrl, { headers });
      const text = await response.text();
      
      console.log('[apiService.get] Response status:', response.status);
      console.log('[apiService.get] Response preview:', text.substring(0, 200));
      
      try {
        return JSON.parse(text);
      } catch (parseErr) {
        console.error('[apiService.get] JSON parse error:', parseErr);
        console.error('[apiService.get] Raw response:', text.substring(0, 500));
        throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
      }
    } catch (err) {
      console.error('[apiService.get] Error:', err);
      throw err;
    }
  },

  async post(endpoint: string, data?: any) {
    const response = await enhancedFetch(joinUrl(API_BASE_URL, endpoint), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: data ? JSON.stringify(data) : undefined
    });
    return response.json();
  },

  async put(endpoint: string, data?: any) {
    const response = await enhancedFetch(joinUrl(API_BASE_URL, endpoint), {
      method: 'PUT',
      headers: await getAuthHeaders(false),
      body: data ? JSON.stringify(data) : undefined
    });
    return response.json();
  },

  async delete(endpoint: string) {
    const response = await enhancedFetch(joinUrl(API_BASE_URL, endpoint), {
      method: 'DELETE',
      headers: await getAuthHeaders(false)
    });
    return response.json();
  },
  // Get available practices
  async getPractices() {
  const response = await fetch(joinUrl(API_BASE_URL, '/api/config/practices'));
    if (!response.ok) throw new Error('Failed to fetch practices');
    return response.json();
  },
  
  // Get available workflows
  async getWorkflows() {
    const practices = await this.getPractices();
    return practices.flatMap((p: any) => p.workflows.map((w: any) => ({ ...w, practiceId: p.id })));
  },
  
  // Get practice details
  async getPracticeDetails(practiceId: string) {
  const response = await fetch(joinUrl(API_BASE_URL, `/api/config/practices/${practiceId}`));
    if (!response.ok) throw new Error('Failed to fetch practice details');
    return response.json();
  },
  
  // Get workflows for a specific practice
  async getWorkflowsForPractice(practiceId: string) {
    const practice = await this.getPracticeDetails(practiceId);
    
    // Enhance workflows with recipe metadata when available
    const enhancedWorkflows = await Promise.all(
      practice.workflows.map(async (workflow: any) => {
        try {
          const recipeData = await this.getRecipeRequirements(practiceId, workflow.id);
          return {
            ...workflow,
            description: recipeData.description || workflow.description,
            requiredFileTypes: this.mapSupportedFormatsToMimeTypes(recipeData.supported_formats) || workflow.requiredFileTypes
          };
        } catch (error) {
          // Fallback to original workflow data if recipe fetch fails
          return workflow;
        }
      })
    );
    
    return enhancedWorkflows;
  },
  
  // Helper to map supported formats to MIME types
  mapSupportedFormatsToMimeTypes(formats: string[] | null) {
    if (!formats) return null;
    
    const mimeTypeMap: Record<string, string> = {
      'pdf': 'application/pdf',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      'json': 'application/json'
    };
    
    return formats.map(format => mimeTypeMap[format.toLowerCase()] || format);
  },
  
  // Upload files to S3 using presigned URLs (bypasses API Gateway limits)
  async uploadFiles(
    files: File[], 
    practice: string, 
    number: string,
    onProgress?: (fileIndex: number, progress: number, fileName: string) => void
  ) {
    console.log('[FRONTEND] Starting file upload for', files.length, 'files');
    
    const s3Uris = [];
    
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      console.log(`[FRONTEND] Processing file ${index + 1}:`, {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Check if file is already uploaded to S3 (from iManage)
      if ((file as any).isS3File && (file as any).s3Uri) {
        console.log('[FRONTEND] File already in S3, using existing URI:', (file as any).s3Uri);
        s3Uris.push((file as any).s3Uri);
        onProgress?.(index, 100, file.name);
        continue;
      }
      
      console.log('[FRONTEND] Using presigned URL upload for:', file.name);
      
      // Get presigned URL (no CSRF needed for this endpoint)
      const presignedResponse = await enhancedFetch(joinUrl(API_BASE_URL, '/api/files/presigned-upload'), {
        method: 'POST',
        headers: await getAuthHeaders(false),
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          practice,
          number
        })
      });
      
      const { presignedUrl, s3Uri } = await presignedResponse.json();
      console.log('[FRONTEND] Got presigned URL, uploading to S3...');
      
      // Use XMLHttpRequest for progress tracking on large files
      if (onProgress && file.size > 10 * 1024 * 1024) { // 10MB threshold for progress tracking
        await this.uploadWithProgress(presignedUrl, file, (progress) => {
          onProgress(index, progress, file.name);
        });
      } else {
        // Standard fetch for smaller files
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file
        });
        
        if (!uploadResponse.ok) {
          console.error('[FRONTEND] S3 upload failed:', uploadResponse.status, uploadResponse.statusText);
          throw new Error(`Failed to upload to S3: ${uploadResponse.status}`);
        }
        onProgress?.(index, 100, file.name);
      }
      
      console.log('[FRONTEND] Successfully uploaded to S3:', s3Uri);
      s3Uris.push(s3Uri);
    }
    
    return {
      s3Uris,
      count: s3Uris.length
    };
  },

  // Upload file with progress tracking using XMLHttpRequest
  async uploadWithProgress(
    presignedUrl: string, 
    file: File, 
    onProgress: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
          console.log(`[FRONTEND] Upload progress for ${file.name}: ${progress}%`);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });
      
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });
      
      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  // Upload a single file to a presigned URL (used by RecipeTestLab)
  async uploadFileToPresignedUrl(presignedUrl: string, file: File): Promise<void> {
    console.log('[FRONTEND] Uploading file to presigned URL:', file.name);
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file
    });
    
    if (!response.ok) {
      console.error('[FRONTEND] S3 upload failed:', response.status, response.statusText);
      throw new Error(`Failed to upload to S3: ${response.status}`);
    }
    console.log('[FRONTEND] Successfully uploaded to S3');
  },
  
  // Execute workflow with S3 file URIs
  async executeWorkflow(params: {
    practice: string;
    workflows: string[];
    number: string;
    input_file_s3_uris: string[];
    workflow_data?: Record<string, any>;
  }) {
  const response = await fetch(joinUrl(API_BASE_URL, '/api/workflows/execute'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify(params)
    });
    if (!response.ok) throw new Error('Failed to execute workflow');
    return response.json();
  },
  
  // Get execution status
  async getExecutionStatus(executionArn: string) {
  const response = await fetch(joinUrl(API_BASE_URL, `/api/workflows/${encodeURIComponent(executionArn)}/status`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get execution status');
    return response.json();
  },
  
  // Get execution results
  async getExecutionResults(executionArn: string) {
    console.log('Fetching results for:', executionArn);
  const response = await fetch(joinUrl(API_BASE_URL, `/api/workflows/${encodeURIComponent(executionArn)}/results`), {
      headers: await getAuthHeaders()
    });
    console.log('Results response status:', response.status);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Results error response:', errorText);
      throw new Error('Failed to get execution results');
    }
    return response.json();
  },
  
  // Get recipe requirements
  async getRecipeRequirements(practice: string, workflow: string) {
  const response = await fetch(joinUrl(API_BASE_URL, `/api/recipes/${practice}/${workflow}/requirements`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get recipe requirements');
    return response.json();
  },
  
  // Get signed URL for downloading a file from S3
  async getSignedDownloadUrl(s3Uri: string, filename?: string) {
  const response = await fetch(joinUrl(API_BASE_URL, '/api/files/download'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ s3Uri, filename })
    });
    if (!response.ok) throw new Error('Failed to get download URL');
    const data = await response.json();
    return data.downloadUrl;
  },

  // proxy file download through our backend to avoid S3 CORS issues
  async downloadFileBlob(s3Uri: string): Promise<Blob> {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/files/download-proxy'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ s3Uri })
    });
    if (!response.ok) throw new Error('Failed to download file');
    return response.blob();
  },

  // get a fresh presigned URL for podcast audio
  async getPodcastAudioUrl(s3Key: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/podcast/audio?s3Key=${encodeURIComponent(s3Key)}`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get podcast audio URL');
    return response.json();
  },

  // Get signed URL for viewing a file (for iframe)
  async getSignedViewUrl(s3Uri: string) {
    // Validate and potentially correct S3 URI for current environment
    const { environmentDataService } = await import('./environmentDataService.js');
    const validation = environmentDataService.validateS3Uri(s3Uri);
    
    if (!validation.isValid && validation.correctedUri) {
      console.warn(`Using environment-corrected S3 URI: ${validation.correctedUri}`);
      s3Uri = validation.correctedUri;
    } else if (!validation.isValid) {
      console.error(`Invalid S3 URI for current environment: ${validation.error}`);
      throw new Error(`File not available in current environment: ${validation.error}`);
    }
    
    const response = await fetch(joinUrl(API_BASE_URL, `/api/files/view?s3Uri=${encodeURIComponent(s3Uri)}`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get view URL');
    return response.json();
  },

  // Get file content via backend proxy (avoids CORS issues)
  async getFileContent(s3Uri: string) {
    try {
      const response = await fetch(joinUrl(API_BASE_URL, '/api/files/content'), {
        method: 'POST',
        headers: await getAuthHeaders(false),
        body: JSON.stringify({ s3Uri })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('File content error:', errorText);
        throw new Error(`Failed to get file content: ${response.status} ${response.statusText}`);
      }
      return response.text();
    } catch (error) {
      console.error('File content fetch error:', error);
      throw error;
    }
  },

  // Submit user input to continue workflow execution
  async submitUserInput(executionArn: string, inputData: Record<string, any>) {
  const response = await fetch(joinUrl(API_BASE_URL, `/api/workflows/${encodeURIComponent(executionArn)}/input`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inputData })
    });
    if (!response.ok) throw new Error('Failed to submit user input');
    return response.json();
  }
};

// Add multi-job methods to real service
const realApiServiceExtended = {
  ...realApiService,
  
  async getAllJobs() {
    const url = joinUrl(API_BASE_URL, '/api/jobs');
    const headers = await getAuthHeaders();
    
    console.log('[apiService.getAllJobs] Fetching jobs from:', url);
    
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[apiService.getAllJobs] API error:', response.status, errorText);
      throw new Error('Failed to fetch jobs');
    }
    const jobs = await response.json();
    
    console.log(`[apiService.getAllJobs] Received ${jobs?.length || 0} jobs from API`);
    
    // Process jobs for environment metadata (no longer filters them out)
    try {
      const { environmentDataService } = await import('./environmentDataService.js');
      const processedJobs = await environmentDataService.filterValidJobs(jobs);
      console.log(`[apiService.getAllJobs] Processed ${processedJobs?.length || 0} jobs`);
      return processedJobs;
    } catch (envError) {
      // If environment processing fails, return raw jobs to ensure visibility
      console.warn('[apiService.getAllJobs] Environment processing failed, returning raw jobs:', envError);
      return jobs;
    }
  },
  
  async pauseJob(jobId: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/jobs/${encodeURIComponent(jobId)}/pause`), {
      method: 'PUT',
      headers: await getAuthHeaders(false)
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Pause job error:', errorText);
      throw new Error('Failed to pause job');
    }
    return response.json();
  },
  
  async resumeJob(jobId: string) {
  const response = await fetch(joinUrl(API_BASE_URL, `/api/jobs/${encodeURIComponent(jobId)}/resume`), {
      method: 'PUT',
      headers: await getAuthHeaders(false)
    });
    if (!response.ok) throw new Error('Failed to resume job');
    return response.json();
  },
  
  async cancelJob(jobId: string) {
  const response = await fetch(joinUrl(API_BASE_URL, `/api/jobs/${encodeURIComponent(jobId)}/cancel`), {
      method: 'PUT',
      headers: await getAuthHeaders(false)
    });
    if (!response.ok) throw new Error('Failed to cancel job');
    return response.json();
  },
  
  async renameJob(jobId: string, newName: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/jobs/${encodeURIComponent(jobId)}/rename`), {
      method: 'PUT',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ name: newName })
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Rename job error:', errorText);
      throw new Error('Failed to rename job');
    }
    return response.json();
  },
  
  async deleteJob(jobId: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/jobs/${encodeURIComponent(jobId)}`), {
      method: 'DELETE',
      headers: await getAuthHeaders(false)
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete job error:', errorText);
      throw new Error('Failed to delete job');
    }
    return response.json();
  },

  // AI Chat methods
  async getChatConversations() {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/chat/conversations'), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get conversations');
    return response.json();
  },

  async createChatConversation(title: string) {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/chat/conversations'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ title })
    });
    if (!response.ok) throw new Error('Failed to create conversation');
    const result = await response.json();
    console.log('createChatConversation response:', result);
    return result;
  },

  async getChatConversation(conversationId: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/chat/conversations/${encodeURIComponent(conversationId)}`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get conversation');
    return response.json();
  },

  async deleteChatConversation(conversationId: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/chat/conversations/${encodeURIComponent(conversationId)}`), {
      method: 'DELETE',
      headers: await getAuthHeaders(false)
    });
    if (!response.ok) throw new Error('Failed to delete conversation');
    return response.json();
  },

  async getChatMessages(conversationId: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get messages');
    return response.json();
  },

  async sendChatMessage(conversationId: string, content: string, model?: string, attachments?: any[], agentConfig?: any) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ content, model, attachments, agentConfig })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Chat API Error:', response.status, errorText);
      throw new Error(`Chat API Error: ${response.status} - ${errorText}`);
    }
    
    return response.json();
  },

  async searchChatConversations(query: string) {
    const response = await fetch(joinUrl(API_BASE_URL, `/api/chat/conversations/search?q=${encodeURIComponent(query)}`), {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to search conversations');
    return response.json();
  },

  async compressChatHistory(conversationId: string) {
    const url = joinUrl(API_BASE_URL, `/api/chat/conversations/${encodeURIComponent(conversationId)}/compress`);
    console.log('📡 Compression API URL:', url);
    
    const headers = await getAuthHeaders(false);
    console.log('🔑 Compression headers:', headers);
    
    const response = await fetch(url, {
      method: 'POST',
      headers
    });
    
    console.log('📊 Compression response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Compression API error:', errorText);
      throw new Error(`Failed to compress conversation history: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log('✅ Compression API result:', result);
    return result;
  },

  async analyzeDocument(fileName: string, s3Uri: string) {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/documents/analyze'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ fileName, s3Uri })
    });
    if (!response.ok) throw new Error('Failed to analyze document');
    return response.json();
  },

  async analyzeDocuments(files: { fileName: string; s3Uri: string }[]) {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/documents/analyze-multiple'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ files })
    });
    if (!response.ok) throw new Error('Failed to analyze documents');
    return response.json();
  },

  async saveJsonToS3(jsonData: any, jobName: string, options?: { practice?: string; wrapInResponses?: boolean }) {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/files/save-json'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ jsonData, jobName, practice: options?.practice, wrapInResponses: options?.wrapInResponses })
    });
    if (!response.ok) throw new Error('Failed to save JSON to S3');
    return response.json();
  },

  async saveFileContent(s3Uri: string, content: string) {
    const response = await fetch(joinUrl(API_BASE_URL, '/api/files/save-content'), {
      method: 'POST',
      headers: await getAuthHeaders(false),
      body: JSON.stringify({ s3Uri, content })
    });
    if (!response.ok) throw new Error('Failed to save file content');
    return response.json();
  },

  // Get forms published to sidebar for current user
  async getSidebarForms() {
    try {
      const response = await enhancedFetch(joinUrl(API_BASE_URL, '/api/schemas/sidebar'), {
        headers: await getAuthHeaders()
      });
      return response.json();
    } catch (error: any) {
      if (error?.status === 404 || error?.status === 500) {
        console.warn('Sidebar forms unavailable, continuing without sidebar schemas:', error.message);
        return { forms: [], total: 0 };
      }
      throw error;
    }
  },

  // Get form submissions (admin only)
  async getFormSubmissions(params?: { schemaId?: string; limit?: number; offset?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.schemaId) queryParams.append('schemaId', params.schemaId);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const url = joinUrl(API_BASE_URL, `/api/submissions${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
    const response = await enhancedFetch(url, {
      headers: await getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch form submissions');
    return response.json();
  },

  // AI Workflow Builder methods
  async createAiBuilderSession(practice: string, workflow: string, title?: string) {
    const url = joinUrl(API_BASE_URL, '/api/studio/ai-builder/sessions');
    const response = await enhancedFetch(url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ practice, workflow, title })
    });
    return response.json();
  },

  async listAiBuilderSessions() {
    const url = joinUrl(API_BASE_URL, '/api/studio/ai-builder/sessions');
    const response = await enhancedFetch(url, { headers: await getAuthHeaders() });
    return response.json();
  },

  async getAiBuilderSession(sessionId: string) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}`);
    const response = await enhancedFetch(url, { headers: await getAuthHeaders() });
    return response.json();
  },

  async deleteAiBuilderSession(sessionId: string) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}`);
    const response = await enhancedFetch(url, {
      method: 'DELETE',
      headers: await getAuthHeaders()
    });
    return response.json();
  },

  async getAiBuilderVersionFiles(sessionId: string, versionNumber: number) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}/versions/${versionNumber}/files`);
    const response = await enhancedFetch(url, { headers: await getAuthHeaders() });
    return response.json();
  },

  // send message to AI builder (synchronous JSON response)
  async sendAiBuilderMessage(sessionId: string, content: string) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}/message`);
    const response = await enhancedFetch(url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ content })
    });
    return response.json();
  },

  // poll for async message completion
  async getAiBuilderMessageStatus(sessionId: string, messageId: string) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}/message-status/${encodeURIComponent(messageId)}`);
    const response = await enhancedFetch(url, { headers: await getAuthHeaders() });
    return response.json();
  },

  async saveAiBuilderManualEdit(sessionId: string, files: Record<string, any>) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}/files`);
    const response = await enhancedFetch(url, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ files })
    });
    return response.json();
  },

  async revertAiBuilderVersion(sessionId: string, versionId: number) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}/revert/${versionId}`);
    const response = await enhancedFetch(url, {
      method: 'POST',
      headers: await getAuthHeaders()
    });
    return response.json();
  },

  async deployAiBuilderSession(sessionId: string) {
    const url = joinUrl(API_BASE_URL, `/api/studio/ai-builder/sessions/${encodeURIComponent(sessionId)}/deploy`);
    const response = await enhancedFetch(url, {
      method: 'POST',
      headers: await getAuthHeaders()
    });
    return response.json();
  }
};

// Export either mock or real service based on environment flag
// Add recipe requirements method to mock service for consistency
const mockServiceExtended = {
  ...mockService,
  // Generic HTTP methods for mock
  async get(endpoint: string, _options?: { params?: Record<string, any> }) {
    // Return admin permissions for demo user
    if (endpoint === '/api/rbac/me') {
      return {
        roles: ['admin', 'user'],
        permissions: [
          { resource: '*', actions: ['*'] },
          { resource: 'workflows', actions: ['create', 'read', 'update', 'delete', 'execute'] },
          { resource: 'jobs', actions: ['create', 'read', 'update', 'delete'] },
          { resource: 'admin', actions: ['read', 'write'] },
          { resource: 'recipes', actions: ['create', 'read', 'update', 'delete', 'sync'] },
        ]
      };
    }
    return {};
  },
  async post(endpoint: string, data?: any) {
    return {};
  },
  async put(endpoint: string, data?: any) {
    return {};
  },
  async delete(endpoint: string) {
    return {};
  },
  
  async getRecipeRequirements(practice: string, workflow: string) {
    // delegate to the mockService implementation, which returns workflow-specific
    // metadata (e.g. the cortese-list address_list ui_element + empty supported_formats).
    return mockService.getRecipeRequirements(practice, workflow);
  },
  async compressChatHistory(conversationId: string) {
    // Mock compression
    return {
      success: true,
      compressedMessages: 15,
      keptMessages: 5,
      summaryTokens: 500
    };
  },

  async analyzeDocument(fileName: string, s3Uri: string) {
    // Mock document analysis
    return {
      fileName,
      documentType: 'contract',
      recommendedWorkflows: ['lease-abstraction'],
      confidence: 0.85,
      summary: 'Mock analysis result'
    };
  },

  async analyzeDocuments(files: { fileName: string; s3Uri: string }[]) {
    // Mock multi-document analysis
    return {
      files: files.map(f => f.fileName),
      collectionType: 'Legal Document Collection',
      confidence: 'High',
      fileCount: files.length,
      recommendedWorkflows: [
        {
          practice: 'litigation',
          workflow: 'complaint-summary',
          reason: 'Collection appears to contain litigation documents'
        }
      ]
    };
  },

  async getSignedViewUrl(s3Uri: string) {
    // Mock signed view URL
    return {
      signedUrl: 'https://example.com/mock-pdf-view'
    };
  },

  async saveJsonToS3(jsonData: any, jobName: string, options?: { practice?: string; wrapInResponses?: boolean }) {
    // Mock save JSON to S3
    const folder = options?.practice ? `step-two-input/${options.practice}` : 'ocean-bank-step-two-input';
    return {
      s3Uri: `s3://mock-bucket/${folder}/${jobName}-reviewed.json`
    };
  },

  async saveFileContent(s3Uri: string, content: string) {
    return { success: true, s3Uri };
  }
};

export const apiService: ApiService = useMockData ? mockServiceExtended : realApiServiceExtended;

